import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { Macro } from "@redotech/effect-api-shared/rpc/macro";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { useLiveQuery } from "@tanstack/react-db";
import { useCallback, useState } from "react";
import {
  useAddTagConversationAction,
  useCloseConversationAction,
  useMarkInProgressConversationAction,
  useReopenConversationAction,
  useSnoozeConversationAction,
} from "../actions/use-conversation-actions";
import { catchApiErrors } from "../db/error-handling";
import { useGlobalConversationCollections } from "../providers/global-conversation-collections-context";
import { useMacros } from "./use-macros";

export interface UseMacroStateResult {
  macros: readonly Macro[] | undefined;
  macroModalOpen: boolean;
  isApplyingMacro: boolean;
  handleMacroClick: () => void;
  handleApplyMacro: (macro: Macro) => Promise<void>;
  handleCloseMacroModal: () => void;
}

export function useMacroState(
  conversationId: ConversationId,
  onApplyContent: (content: string) => void,
): UseMacroStateResult {
  const [macroModalOpen, setMacroModalOpen] = useState(false);
  const [isApplyingMacro, setIsApplyingMacro] = useState(false);

  const { macros } = useMacros();
  const effectApi = useRequiredContext(EffectApiClientContext);
  const collections = useGlobalConversationCollections();
  const { data: allTags } = useLiveQuery(
    (q) => q.from({ tag: collections.tags }),
    [],
  );

  const closeAction = useCloseConversationAction();
  const reopenAction = useReopenConversationAction();
  const snoozeAction = useSnoozeConversationAction();
  const markInProgressAction = useMarkInProgressConversationAction();
  const addTagAction = useAddTagConversationAction();

  const handleMacroClick = useCallback(() => {
    setMacroModalOpen(true);
  }, []);

  const handleCloseMacroModal = useCallback(() => {
    setMacroModalOpen(false);
  }, []);

  const handleApplyMacro = useCallback(
    async (macro: Macro) => {
      setIsApplyingMacro(true);
      try {
        const client = effectApi.getClient();
        const result = await effectApi.runPromise(
          catchApiErrors(
            client.macro.replaceMacroVariables({
              payload: { content: macro.htmlContent, conversationId },
            }),
          ),
        );
        onApplyContent(result.message);
      } catch {
        onApplyContent(macro.htmlContent);
      }

      if (macro.statusToSet) {
        const status = macro.statusToSet.toUpperCase();
        switch (status) {
          case "CLOSED":
            void closeAction.execute(conversationId);
            break;
          case "OPEN":
            void reopenAction.execute(conversationId);
            break;
          case "SNOOZED":
            void snoozeAction.execute(
              conversationId,
              macro.snoozeDuration ?? "1h",
            );
            break;
          case "IN_PROGRESS":
            void markInProgressAction.execute(conversationId);
            break;
        }
      }

      if (macro.tagsToAdd?.length) {
        const tagIds = allTags
          .filter((tag) => macro.tagsToAdd!.includes(tag.name))
          .map((tag) => tag.id);
        if (tagIds.length) {
          void addTagAction.execute(conversationId, { tagIds });
        }
      }

      setIsApplyingMacro(false);
      setMacroModalOpen(false);
    },
    [
      effectApi,
      conversationId,
      onApplyContent,
      allTags,
      closeAction,
      reopenAction,
      snoozeAction,
      markInProgressAction,
      addTagAction,
    ],
  );

  return {
    macros,
    macroModalOpen,
    isApplyingMacro,
    handleMacroClick,
    handleApplyMacro,
    handleCloseMacroModal,
  };
}
